import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import {
  Search,
  Filter,
  Calendar,
  User,
  FileText,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { toast } from 'sonner'

interface AuditLog {
  id: string
  user_id: string
  user_email: string
  action_type: 'view' | 'create' | 'update' | 'delete' | 'download' | 'upload'
  resource_type: 'medical_record' | 'document' | 'user_profile' | 'access_control' | 'audit_log'
  resource_id?: string
  resource_description: string
  accessed_data: Record<string, any>
  ip_address?: string
  user_agent?: string
  session_id?: string
  metadata?: Record<string, any>
  created_at: string
  user_profile?: {
    full_name: string
    role: string
  }
}

interface AuditLogsViewerProps {
  patientId?: string
  doctorId?: string
  resourceType?: string
  resourceId?: string
}

const ACTION_ICONS = {
  view: Eye,
  create: Upload,
  update: Edit,
  delete: Trash2,
  download: Download,
  upload: Upload
}

const ACTION_COLORS = {
  view: 'text-blue-600 bg-blue-100',
  create: 'text-green-600 bg-green-100',
  update: 'text-yellow-600 bg-yellow-100',
  delete: 'text-red-600 bg-red-100',
  download: 'text-purple-600 bg-purple-100',
  upload: 'text-indigo-600 bg-indigo-100'
}

const RESOURCE_ICONS = {
  medical_record: FileText,
  document: FileText,
  user_profile: User,
  access_control: Shield,
  audit_log: Clock
}

export default function AuditLogsViewer({
  patientId,
  doctorId,
  resourceType,
  resourceId
}: AuditLogsViewerProps) {
  const { user } = useAuth()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    action_type: '',
    resource_type: '',
    date_from: '',
    date_to: '',
    user_id: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [expandedLog, setExpandedLog] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const logsPerPage = 20

  useEffect(() => {
    loadAuditLogs()
  }, [filters, currentPage, patientId, doctorId, resourceType, resourceId])

  const loadAuditLogs = async () => {
    if (!user) return

    try {
      setLoading(true)
      
      let query = supabase
        .from('audit_logs')
        .select(`
          *,
          user_profile:user_profiles!audit_logs_user_id_fkey(
            full_name,
            role
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false })

      // Aplicar filtros específicos
      if (patientId) {
        query = query.or(`metadata->>patient_id.eq.${patientId},accessed_data->>patient_id.eq.${patientId}`)
      }
      
      if (doctorId) {
        query = query.or(`metadata->>doctor_id.eq.${doctorId},accessed_data->>doctor_id.eq.${doctorId}`)
      }
      
      if (resourceType) {
        query = query.eq('resource_type', resourceType)
      }
      
      if (resourceId) {
        query = query.eq('resource_id', resourceId)
      }

      // Aplicar filtros do usuário
      if (filters.action_type) {
        query = query.eq('action_type', filters.action_type)
      }
      
      if (filters.resource_type) {
        query = query.eq('resource_type', filters.resource_type)
      }
      
      if (filters.user_id) {
        query = query.eq('user_id', filters.user_id)
      }
      
      if (filters.date_from) {
        query = query.gte('created_at', filters.date_from)
      }
      
      if (filters.date_to) {
        query = query.lte('created_at', filters.date_to + 'T23:59:59')
      }

      // Paginação
      const from = (currentPage - 1) * logsPerPage
      const to = from + logsPerPage - 1
      query = query.range(from, to)

      const { data, error, count } = await query
      
      if (error) {
        toast.error('Erro ao carregar logs de auditoria')
        console.error(error)
        return
      }

      // Filtrar por termo de busca
      let filteredData = data || []
      if (searchTerm) {
        filteredData = filteredData.filter(log =>
          log.resource_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.user_profile?.full_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }

      setLogs(filteredData)
      setTotalPages(Math.ceil((count || 0) / logsPerPage))
    } catch (error) {
      toast.error('Erro ao carregar logs de auditoria')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getActionIcon = (actionType: string) => {
    const IconComponent = ACTION_ICONS[actionType as keyof typeof ACTION_ICONS] || AlertTriangle
    return <IconComponent className="h-4 w-4" />
  }

  const getResourceIcon = (resourceType: string) => {
    const IconComponent = RESOURCE_ICONS[resourceType as keyof typeof RESOURCE_ICONS] || FileText
    return <IconComponent className="h-4 w-4" />
  }

  const exportLogs = async () => {
    try {
      const csvContent = [
        ['Data/Hora', 'Usuário', 'Ação', 'Recurso', 'Descrição', 'IP', 'User Agent'].join(','),
        ...logs.map(log => [
          formatDate(log.created_at),
          log.user_profile?.full_name || log.user_email,
          log.action_type,
          log.resource_type,
          `"${log.resource_description}"`,
          log.ip_address || '',
          `"${log.user_agent || ''}"`
        ].join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      toast.success('Logs exportados com sucesso')
    } catch (error) {
      toast.error('Erro ao exportar logs')
      console.error(error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Logs de Auditoria</h2>
          <p className="text-gray-600">Registro de acessos e ações conforme LGPD</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </button>
          <button
            onClick={exportLogs}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </button>
          <button
            onClick={loadAuditLogs}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Filtros */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar logs..."
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ação
              </label>
              <select
                value={filters.action_type}
                onChange={(e) => setFilters(prev => ({ ...prev, action_type: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Todas as ações</option>
                <option value="view">Visualizar</option>
                <option value="create">Criar</option>
                <option value="update">Atualizar</option>
                <option value="delete">Excluir</option>
                <option value="download">Download</option>
                <option value="upload">Upload</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recurso
              </label>
              <select
                value={filters.resource_type}
                onChange={(e) => setFilters(prev => ({ ...prev, resource_type: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Todos os recursos</option>
                <option value="medical_record">Registro Médico</option>
                <option value="document">Documento</option>
                <option value="user_profile">Perfil de Usuário</option>
                <option value="access_control">Controle de Acesso</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Início
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Fim
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Lista de Logs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Logs de Auditoria ({logs.length})
          </h3>
        </div>
        
        {logs.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm || Object.values(filters).some(f => f)
                ? 'Nenhum log encontrado com os filtros aplicados'
                : 'Nenhum log de auditoria encontrado'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log) => (
              <div key={log.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${ACTION_COLORS[log.action_type]}`}>
                      {getActionIcon(log.action_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-medium text-gray-900">
                          {log.resource_description}
                        </h4>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${ACTION_COLORS[log.action_type]}`}>
                          {log.action_type.toUpperCase()}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <User className="h-4 w-4" />
                          <span>{log.user_profile?.full_name || log.user_email}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          {getResourceIcon(log.resource_type)}
                          <span>{log.resource_type.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>{formatDate(log.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {expandedLog === log.id ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                </div>
                
                {expandedLog === log.id && (
                  <div className="mt-4 pl-11">
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      {log.ip_address && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">IP: </span>
                          <span className="text-sm text-gray-600">{log.ip_address}</span>
                        </div>
                      )}
                      {log.session_id && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">Sessão: </span>
                          <span className="text-sm text-gray-600 font-mono">{log.session_id}</span>
                        </div>
                      )}
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">Metadados: </span>
                          <pre className="text-xs text-gray-600 bg-white p-2 rounded border mt-1 overflow-x-auto">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log.accessed_data && Object.keys(log.accessed_data).length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">Dados Acessados: </span>
                          <pre className="text-xs text-gray-600 bg-white p-2 rounded border mt-1 overflow-x-auto">
                            {JSON.stringify(log.accessed_data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Paginação */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Página {currentPage} de {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}